import { Alert, Form, Input, message, Modal, Select } from "antd";
import Service, { ElAdminEventLogService } from "../../services";
import React, { useEffect, useState } from "react";
import { determineTradeTypeByExchangeInstrument, EXCHANGE_INSTRUMENT, INSTRUMENT, naturalSortSessions } from "../../utils";

export const ACCOUNT_MANAGEMENT_MODAL_COMMAND = {
  NEW_ACCOUNT: "New Trading Account",
  DELETE_ACCOUNT: "Delete Session/Account",
  NEW_SESSION: "New Trading Session",
};

const { Option } = Select;

// used in exchange <Select><Option> jsx
const EXCHANGE_INSTRUMENT_SELECT_OPTIONS = [
  {
    label: "Binance USD-M future",
    value: EXCHANGE_INSTRUMENT.binance_usds_future,
    exchange: "binance",
    whenDisplay: [
      ACCOUNT_MANAGEMENT_MODAL_COMMAND.NEW_ACCOUNT,
      ACCOUNT_MANAGEMENT_MODAL_COMMAND.NEW_SESSION,
      ACCOUNT_MANAGEMENT_MODAL_COMMAND.DELETE_ACCOUNT,
    ],
  },
  {
    label: "Binance Coin-M future",
    value: EXCHANGE_INSTRUMENT.binance_coin_future,
    exchange: "binance",
    whenDisplay: [ACCOUNT_MANAGEMENT_MODAL_COMMAND.NEW_SESSION, ACCOUNT_MANAGEMENT_MODAL_COMMAND.DELETE_ACCOUNT],
  },
  {
    label: "Binance Spot",
    value: EXCHANGE_INSTRUMENT.binance_spot,
    exchange: "binance",
    whenDisplay: [ACCOUNT_MANAGEMENT_MODAL_COMMAND.NEW_SESSION, ACCOUNT_MANAGEMENT_MODAL_COMMAND.DELETE_ACCOUNT],
  },
  {
    label: "Binance Spot Algo",
    value: EXCHANGE_INSTRUMENT.binance_spot_algo,
    exchange: "binance",
    whenDisplay: [ACCOUNT_MANAGEMENT_MODAL_COMMAND.NEW_SESSION, ACCOUNT_MANAGEMENT_MODAL_COMMAND.DELETE_ACCOUNT],
  },
  {
    label: "Binance Future Algo",
    value: EXCHANGE_INSTRUMENT.binance_future_algo,
    exchange: "binance",
    whenDisplay: [ACCOUNT_MANAGEMENT_MODAL_COMMAND.NEW_SESSION, ACCOUNT_MANAGEMENT_MODAL_COMMAND.DELETE_ACCOUNT],
  },
  {
    label: "Binance Funding Rate Arbitrage",
    value: EXCHANGE_INSTRUMENT.binance_funding_rate_arbitrage,
    exchange: "binance",
    whenDisplay: [ACCOUNT_MANAGEMENT_MODAL_COMMAND.NEW_SESSION, ACCOUNT_MANAGEMENT_MODAL_COMMAND.DELETE_ACCOUNT],
  },
  {
    label: "OKX USD-M future",
    value: EXCHANGE_INSTRUMENT.okx_usds_future,
    exchange: "okx",
    whenDisplay: [
      ACCOUNT_MANAGEMENT_MODAL_COMMAND.NEW_ACCOUNT,
      ACCOUNT_MANAGEMENT_MODAL_COMMAND.NEW_SESSION,
      ACCOUNT_MANAGEMENT_MODAL_COMMAND.DELETE_ACCOUNT,
    ],
  },
  {
    label: "Okx Spot Algo",
    value: EXCHANGE_INSTRUMENT.okx_spot_algo,
    exchange: "okx",
    whenDisplay: [ACCOUNT_MANAGEMENT_MODAL_COMMAND.NEW_SESSION, ACCOUNT_MANAGEMENT_MODAL_COMMAND.DELETE_ACCOUNT],
  },
  {
    label: "OKX Funding Rate",
    value: EXCHANGE_INSTRUMENT.okx_funding_rate_arbitrage,
    exchange: "okx",
    whenDisplay: [ACCOUNT_MANAGEMENT_MODAL_COMMAND.NEW_SESSION, ACCOUNT_MANAGEMENT_MODAL_COMMAND.DELETE_ACCOUNT],
  },
  {
    label: "CME future",
    value: EXCHANGE_INSTRUMENT.cme_tradestation_future,
    exchange: "cme",
    whenDisplay: [
      ACCOUNT_MANAGEMENT_MODAL_COMMAND.NEW_ACCOUNT,
      ACCOUNT_MANAGEMENT_MODAL_COMMAND.NEW_SESSION,
      ACCOUNT_MANAGEMENT_MODAL_COMMAND.DELETE_ACCOUNT,
    ],
  },
  {
    label: "Crypto.com USD-M future",
    value: EXCHANGE_INSTRUMENT.cryptocom_usds_future,
    exchange: "cryptocom",
    whenDisplay: [ACCOUNT_MANAGEMENT_MODAL_COMMAND.NEW_ACCOUNT, ACCOUNT_MANAGEMENT_MODAL_COMMAND.DELETE_ACCOUNT],
  },
  {
    label: "Route28 USD-M future",
    value: EXCHANGE_INSTRUMENT.route28_usds_future,
    exchange: "route28",
    whenDisplay: [
      ACCOUNT_MANAGEMENT_MODAL_COMMAND.NEW_ACCOUNT,
      ACCOUNT_MANAGEMENT_MODAL_COMMAND.NEW_SESSION,
      ACCOUNT_MANAGEMENT_MODAL_COMMAND.DELETE_ACCOUNT,
    ],
  },
];

const AccountManagementModal = ({ accountManagementCommand, exchangeAccount, closeModal, accountMap, onConfirmCallback }) => {
  const [form] = Form.useForm();
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [rsaPublicKey, setRsaPublicKey] = useState("");
  const [tradeSessionNameSet, setTradeSessionNameSet] = useState(new Set());
  const exchangeInstrument = Form.useWatch("exchangeInstrument", form);

  const isApiKeySectionVisible = accountManagementCommand === ACCOUNT_MANAGEMENT_MODAL_COMMAND.NEW_ACCOUNT;

  useEffect(() => {
    async function fetchRsaPublicKey() {
      const rsaPublicKey = await Service.fetchRsaPublicKey();
      setRsaPublicKey(rsaPublicKey);
    }
    if (isApiKeySectionVisible) {
      fetchRsaPublicKey();
    }
  }, [isApiKeySectionVisible]);

  useEffect(() => {
    async function fetchTradeSessionNameSet() {
      const tradeSessionNameSet = await Service.fetchTradeSessionNameSet();
      setTradeSessionNameSet(tradeSessionNameSet);
    }
    fetchTradeSessionNameSet();
  }, []);

  const layout = {
    labelCol: { span: 8 },
    wrapperCol: { span: 13 },
  };

  const exchangePrefix = accountManagementCommand !== ACCOUNT_MANAGEMENT_MODAL_COMMAND.NEW_ACCOUNT ? exchangeAccount?.split("_")[0] : null;

  // Filter the options based on the conditions
  const exchangeSelectOptions = EXCHANGE_INSTRUMENT_SELECT_OPTIONS.filter(
    (item) => (!exchangePrefix || item.exchange === exchangePrefix) && item.whenDisplay?.includes(accountManagementCommand)
  );

  const sessionData = accountMap?.get(exchangeAccount);
  let sessionSelectOptions = [];
  if (exchangeInstrument) {
    const type = determineTradeTypeByExchangeInstrument(exchangeInstrument);
    if (type === INSTRUMENT.usds_future || exchangeInstrument === INSTRUMENT.cme_future) {
      sessionSelectOptions = sessionData
        ? [
            ...sessionData.usdSession.map((session) => session.split("_").slice(1).join("_")),
            exchangeAccount?.split("_").slice(1).join("_"),
          ]
        : [exchangeAccount?.split("_").slice(1).join("_")];
    } else if (type === INSTRUMENT.coin_future) {
      sessionSelectOptions = sessionData ? [...sessionData.coinSession] : [];
    } else if (type === INSTRUMENT.spot) {
      sessionSelectOptions = sessionData ? [...sessionData.spotSession] : [];
    } else if (type === INSTRUMENT.spot_algo) {
      sessionSelectOptions = sessionData ? [...sessionData.spotAlgoSession] : [];
    } else if (type === INSTRUMENT.future_algo) {
      sessionSelectOptions = sessionData ? [...sessionData.futureAlgoSession] : [];
    } else if (type === INSTRUMENT.funding_rate_arbitrage) {
      sessionSelectOptions = sessionData ? [...sessionData.fundingRateArbitrageSession] : [];
    }
    sessionSelectOptions.sort(naturalSortSessions);
  }

  const exchangeInstrumentAndAccountOnChangeHandler = (form) => {
    const session = form.getFieldValue("session");
    const exchangeInstrument = form.getFieldValue("exchangeInstrument");
    const exchange = EXCHANGE_INSTRUMENT_SELECT_OPTIONS.find(({ value }) => value === exchangeInstrument)?.exchange;
    const exchangeAccount = `${exchange}_${session}`;
    const sessionName = `${exchangeInstrument}_${session}`;
    form.setFieldsValue({ exchangeAccount, sessionName });
  };

  return (
    <Modal
      width={640}
      open={true}
      confirmLoading={confirmLoading}
      title={accountManagementCommand}
      okText="Confirm"
      cancelText="Cancel"
      onCancel={closeModal}
      onOk={() => {
        form
          .validateFields()
          .then((formParams) => {
            setConfirmLoading(true);
            if (accountManagementCommand === ACCOUNT_MANAGEMENT_MODAL_COMMAND.NEW_ACCOUNT) {
              if (tradeSessionNameSet.has(formParams.sessionName)) {
                throw new Error(`Session Name ${formParams.sessionName} already exist.`);
              }
              return Service.createTradeAccount({ rsaPublicKey, ...formParams });
            } else if (accountManagementCommand === ACCOUNT_MANAGEMENT_MODAL_COMMAND.NEW_SESSION) {
              const parentAccount = formParams.parentAccount;
              const sessionName = formParams.sessionName;
              if (tradeSessionNameSet.has(sessionName)) {
                throw new Error(`Session Name ${sessionName} already exist.`);
              }
              const tradeType = determineTradeTypeByExchangeInstrument(exchangeInstrument);
              const exchange = EXCHANGE_INSTRUMENT_SELECT_OPTIONS.find(({ value }) => value === exchangeInstrument)?.exchange;
              if (tradeType === INSTRUMENT.coin_future || tradeType === INSTRUMENT.spot) {
                const usdsSessionName = `${exchange}_${INSTRUMENT.usds_future}_${formParams.session}`;
                if (!tradeSessionNameSet.has(usdsSessionName)) {
                  throw new Error(`Please create the USDS future session ${usdsSessionName} before creating the ${sessionName} session.`);
                }
              }
              return Service.createTradeSession({ ...formParams, parentAccount, sessionName });
            } else if (accountManagementCommand === ACCOUNT_MANAGEMENT_MODAL_COMMAND.DELETE_ACCOUNT) {
              if (!tradeSessionNameSet.has(formParams.sessionName)) {
                throw new Error(`Session Name ${formParams.sessionName} does NOT exist.`);
              }
              if (
                formParams.sessionName.includes(EXCHANGE_INSTRUMENT.binance_usds_future) ||
                formParams.sessionName.includes(EXCHANGE_INSTRUMENT.okx_usds_future)
              ) {
                const sessionNameCoinFuture = formParams.sessionName.replace(INSTRUMENT.usds_future, INSTRUMENT.coin_future);
                const sessionNameSpotAlgo = formParams.sessionName.replace(INSTRUMENT.usds_future, INSTRUMENT.spot_algo);
                const sessionNameFutureAlgo = formParams.sessionName.replace(INSTRUMENT.usds_future, INSTRUMENT.future_algo);
                const sessionNameSpot = formParams.sessionName.replace(INSTRUMENT.usds_future, INSTRUMENT.spot);
                const sessionNameFundingRateArbitrage = formParams.sessionName.replace(
                  INSTRUMENT.usds_future,
                  INSTRUMENT.funding_rate_arbitrage
                );
                if (Array.from(tradeSessionNameSet.values()).find((name) => name.includes(sessionNameCoinFuture))) {
                  throw new Error(`Please delete COIN-M session before deleting USD-M session.`);
                }
                if (Array.from(tradeSessionNameSet.values()).find((name) => name.includes(sessionNameSpotAlgo))) {
                  throw new Error(`Please delete Spot Algo session before deleting USD-M session.`);
                }
                if (Array.from(tradeSessionNameSet.values()).find((name) => name.includes(sessionNameFutureAlgo))) {
                  throw new Error(`Please delete Future Algo session before deleting USD-M session.`);
                }
                if (Array.from(tradeSessionNameSet.values()).find((name) => name.includes(sessionNameSpot))) {
                  throw new Error(`Please delete Spot session before deleting USD-M session.`);
                }
                if (Array.from(tradeSessionNameSet.values()).find((name) => name.includes(sessionNameFundingRateArbitrage))) {
                  throw new Error(`Please delete Funding Rate Arbitrage session before deleting USD-M session.`);
                }
              }
              const parentInfo = accountMap.get(formParams.exchangeAccount);
              if (parentInfo && parentInfo.usdSession.length > 0) {
                throw new Error("Please delete sub USD-M sessions before deleting the parent USD-M session.");
              }
              return Service.deleteTradeAccount({ ...formParams });
            } else {
              throw new Error(`Wrong command: ${accountManagementCommand}`);
            }
          })
          .then((response) => {
            if (!response || typeof response !== "string") {
              console.log({ response });
              throw new Error(`Something is wrong: response is malformed`);
            }
            return ElAdminEventLogService.saveAccountManagementEvent(
              accountManagementCommand,
              form.getFieldValue("session"),
              form.getFieldValue("exchangeInstrument")
            ).then(() => {
              closeModal();
              if (onConfirmCallback) {
                onConfirmCallback();
              }
              message.success(response);
            });
          })
          .catch((error) => {
            console.log("Validate Failed:", error);
            console.log(error);
            message.error(error.message ? error.message : String(error));
            setConfirmLoading(false);
          });
      }}
    >
      {accountManagementCommand === ACCOUNT_MANAGEMENT_MODAL_COMMAND.DELETE_ACCOUNT && (
        <section style={{ marginBottom: 26 }}>
          <Alert
            showIcon={true}
            type="warning"
            message="Warning Message"
            description={
              <code>
                1. Please delete Spot, Spot Algo, Future Algo, and COIN-M sessions before deleting USD-M session.
                <br />
                2. Please delete sub USD-M sessions before deleting the parent USD-M session.
                <br />
                3. Deleting USD-M session will also delete your trading account.
              </code>
            }
          />
        </section>
      )}
      <Form
        {...layout}
        form={form}
        initialValues={{
          exchangeAccount,
          parentAccount: exchangeAccount && exchangeAccount.substring(exchangeAccount.indexOf("_") + 1, exchangeAccount.length),
          session: exchangeAccount && exchangeAccount.substring(exchangeAccount.indexOf("_") + 1, exchangeAccount.length),
        }}
      >
        <Form.Item label="Trading Account" name="exchangeAccount" rules={[{ required: true }]}>
          <Input disabled={true} />
        </Form.Item>
        <Form.Item label="Session Name" name="sessionName" rules={[{ required: true }]}>
          <Input disabled={true} />
        </Form.Item>
        <Form.Item
          label="Exchange Instrument"
          name="exchangeInstrument"
          rules={[{ required: true, message: "Please select exchange instrument" }]}
        >
          <Select placeholder="Please select exchange instrument" onChange={() => exchangeInstrumentAndAccountOnChangeHandler(form)}>
            {exchangeSelectOptions.map((option, index) => (
              <Option key={index} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        </Form.Item>
        {accountManagementCommand !== ACCOUNT_MANAGEMENT_MODAL_COMMAND.NEW_ACCOUNT && (
          <Form.Item
            label={"Parent Account"}
            name="parentAccount"
            rules={[{ required: true, message: "Please input Parent Account", type: "string" }]}
            onChange={() => exchangeInstrumentAndAccountOnChangeHandler(form)}
          >
            <Input
              disabled={accountManagementCommand !== ACCOUNT_MANAGEMENT_MODAL_COMMAND.NEW_ACCOUNT}
              placeholder="Please input Parent Account"
            />
          </Form.Item>
        )}
        <Form.Item
          label={accountManagementCommand === ACCOUNT_MANAGEMENT_MODAL_COMMAND.NEW_ACCOUNT ? "Account" : "Session"}
          name="session"
          rules={[{ required: true, message: "Please input account", type: "string" }]}
          help={
            accountManagementCommand !== ACCOUNT_MANAGEMENT_MODAL_COMMAND.NEW_SESSION
              ? ""
              : "Please follow the same naming style as the parent account."
          }
        >
          {accountManagementCommand === ACCOUNT_MANAGEMENT_MODAL_COMMAND.DELETE_ACCOUNT ? (
            <Select placeholder="Please select a session" onChange={() => exchangeInstrumentAndAccountOnChangeHandler(form)}>
              {sessionSelectOptions.map((session, index) => (
                <Option key={index} value={session}>
                  {session}
                </Option>
              ))}
            </Select>
          ) : (
            <Input placeholder="Please input account" onChange={() => exchangeInstrumentAndAccountOnChangeHandler(form)} />
          )}
        </Form.Item>
        {isApiKeySectionVisible && (
          <section className="apiKeySection">
            <Form.Item
              label="Read API Key"
              name="readApiKey"
              rules={[{ required: true, message: "Please input read api key", type: "string" }]}
            >
              <Input placeholder="input read api key" />
            </Form.Item>
            <Form.Item
              label="Read API Secret"
              name="readApiSecret"
              rules={[{ required: true, message: "Please input read api secret", type: "string" }]}
            >
              <Input placeholder="input read api secret" />
            </Form.Item>
            {exchangeInstrument === EXCHANGE_INSTRUMENT.okx_usds_future && (
              <Form.Item
                label="Read API Passphrase"
                name="readApiPassword"
                rules={[{ required: false, message: "Please input read api passphrase", type: "string" }]}
              >
                <Input placeholder="input read api passphrase" />
              </Form.Item>
            )}
            <Form.Item
              label="Trade API Key"
              name="tradeApiKey"
              rules={[{ required: true, message: "Please input trade api key", type: "string" }]}
            >
              <Input placeholder="input trade api key" />
            </Form.Item>
            <Form.Item
              label="Trade API Secret"
              name="tradeApiSecret"
              rules={[{ required: true, message: "Please input trade api secret", type: "string" }]}
            >
              <Input placeholder="input trade api secret" />
            </Form.Item>
            {exchangeInstrument === EXCHANGE_INSTRUMENT.okx_usds_future && (
              <Form.Item
                label="Trade API Passphrase"
                name="tradeApiPassword"
                rules={[{ required: false, message: "Please input trade api passphrase", type: "string" }]}
              >
                <Input placeholder="input trade api passphrase" />
              </Form.Item>
            )}
          </section>
        )}
      </Form>
    </Modal>
  );
};

export default AccountManagementModal;
